import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Plus, Edit, Trash2, Shield, Loader2, Info } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

interface Administrator {
  id: string;
  idNumber: string;
  name: string;
  email: string;
  department: string;
  role: 'administrator';
}

export function AdminManagement() {
  const { accessToken } = useAuth();
  const [administrators, setAdministrators] = useState<Administrator[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Administrator | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    idNumber: '',
    name: '',
    email: '',
    department: ''
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const token = accessToken || '';
      const data = await api.getAdmins(token);
      setAdministrators(data.admins);
    } catch (error) {
      console.error('Error fetching administrators:', error);
      toast.error('Failed to load administrators');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const token = accessToken || '';
      
      if (editingAdmin) {
        await api.updateAdmin(editingAdmin.id, formData, token);
        toast.success('Administrator updated successfully');
      } else {
        await api.createAdmin(formData, token);
        toast.success('Administrator created successfully');
      }
      
      await fetchAdmins();
      handleDialogClose();
    } catch (error) {
      console.error('Error saving administrator:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save administrator';
      
      // Show helpful message for duplicate email errors
      if (errorMessage.includes('already registered') || errorMessage.includes('already exists')) {
        toast.error('This email address is already in use. The system will attempt to clean up and retry. Please try again in a moment.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (admin: Administrator) => {
    setEditingAdmin(admin);
    setFormData({
      idNumber: admin.idNumber,
      name: admin.name,
      email: admin.email,
      department: admin.department
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (adminId: string) => {
    if (administrators.length <= 1) {
      toast.error('Cannot delete the last administrator account');
      return;
    }

    if (!confirm('Are you sure you want to delete this administrator?')) {
      return;
    }
    
    try {
      const token = accessToken || '';
      await api.deleteAdmin(adminId, token);
      toast.success('Administrator deleted successfully');
      await fetchAdmins();
    } catch (error) {
      console.error('Error deleting administrator:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete administrator');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingAdmin(null);
    setFormData({
      idNumber: '',
      name: '',
      email: '',
      department: ''
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Administrator Management</CardTitle>
            <CardDescription>Manage system administrators and their access</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Administrator
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingAdmin ? 'Edit Administrator' : 'Add New Administrator'}
                </DialogTitle>
                <DialogDescription>
                  {editingAdmin 
                    ? 'Update the administrator information below' 
                    : 'Fill in the details to create a new administrator account'
                  }
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="idNumber">ID Number</Label>
                  <Input
                    id="idNumber"
                    value={formData.idNumber}
                    onChange={(e) => setFormData({...formData, idNumber: e.target.value})}
                    placeholder="e.g., ADMIN002"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Jane Administrator"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="e.g., jane.admin@nemsu.edu.ph"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    placeholder="e.g., IT Services"
                    required
                  />
                </div>
                {!editingAdmin && (
                  <Alert className="bg-amber-50 border-amber-200">
                    <Info className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800 text-sm">
                      The new administrator will be created with the default password <strong>admin123</strong>. They should change it after their first login.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={handleDialogClose} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editingAdmin ? 'Update' : 'Add'} Administrator
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : administrators.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No administrators found. Add your first administrator to get started.
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID Number</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {administrators.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Shield className="w-4 h-4 text-orange-500" />
                        <Badge variant="outline">{admin.idNumber}</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{admin.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{admin.email}</TableCell>
                    <TableCell>{admin.department}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(admin)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(admin.id)}
                          disabled={administrators.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {administrators.length <= 1 && (
              <p className="text-sm text-muted-foreground mt-4">
                Note: At least one administrator account must exist.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
